"use client";
import { useState } from "react";
import Input from "@/components/ui/Input";
import { cn } from "@/lib/utils/classNames";
import { useRouter } from "next/navigation";
import { useLanguage } from "@/contexts/LanguageContext";
import { beLogin, setSession } from "@/lib/api/be";
import { COMMON_IMAGES } from "@/lib/publicAssetRoots";
import { lobbyHomePath, seotdaPath } from "@/lib/routes/appPaths";
import { formatBuildDateLabel } from "@/lib/utils/buildDateLabel";

/** Intentional no-op for CI/CD deploy triggers (login touchpoint). */
export function ciCdLoginTouchpointNoop(): void {
  void 0;
}

const LoginPageClient = () => {
  const { t, language, setLanguage } = useLanguage();
  /* =========HOOKS======== */
  // Router
  const router = useRouter();
  /* =========STATE======== */
  // Form Data
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });

  // Errors
  const [errors, setErrors] = useState<{
    username?: string;
    password?: string;
  }>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const buildDateLabel = formatBuildDateLabel();

  // ============HANDLERS===========
  const handleInputChange =
    (field: "username" | "password") =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFormData((prev) => ({
          ...prev,
          [field]: value,
        }));

        // Clear error when user starts typing
        if (errors[field]) {
          setErrors((prev) => ({
            ...prev,
            [field]: undefined,
          }));
        }
      };

  const validateForm = (): boolean => {
    const newErrors: { username?: string; password?: string } = {};

    if (!formData.username.trim()) {
      newErrors.username = t("auth.errors.usernameRequired");
    } else if (formData.username.trim().length < 3) {
      newErrors.username = t("auth.errors.usernameMinLength");
    }

    if (!formData.password) {
      newErrors.password = t("auth.errors.passwordRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = () => {
    void (async () => {
      setApiError(null);
      if (!validateForm()) return;
      setLoading(true);
      try {
        const res = await beLogin(formData);
        if (!res.ok) {
          const code = (res as { error?: string }).error || "";
          if (code === "PENDING_APPROVAL") {
            setApiError(t("auth.pendingApproval"));
            return;
          }
          const msg = (res as { message?: string }).message || res.error || t("auth.errors.loginFailed");
          setApiError(msg);
          return;
        }
        setSession({ token: res.token, userId: res.user.id, username: res.user.username });
        router.push(lobbyHomePath());
      } catch {
        setApiError(t("auth.errors.networkError"));
      } finally {
        setLoading(false);
      }
    })();
  };

  return (
    <div className="relative min-h-dvh w-full overflow-hidden bg-[#05070d]">
      <div className="absolute inset-0 z-0">
        <img
          src={`${COMMON_IMAGES}/lobby/loginback.png`}
          alt="login background"
          className="h-full w-full object-cover"
          draggable={false}
        />
      </div>
      <div className="pointer-events-none absolute inset-0 z-0 bg-black/45" />
      <header className="sticky top-0 z-20 border-b border-white/10 bg-[#0f1522]/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4">
          <h1 className="text-lg font-bold tracking-wide text-white">{t("auth.login")}</h1>
          <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setLanguage("ko")}
            data-selected={language === "ko" ? "true" : undefined}
            aria-pressed={language === "ko"}
            className={cn(
              "ui-glass-btn px-2 py-1 text-xs font-medium rounded",
              language !== "ko" && "text-[#a49e92] hover:text-[#f5bc8e]"
            )}
          >
            KO
          </button>
          <button
            type="button"
            onClick={() => setLanguage("en")}
            data-selected={language === "en" ? "true" : undefined}
            aria-pressed={language === "en"}
            className={cn(
              "ui-glass-btn px-2 py-1 text-xs font-medium rounded",
              language !== "en" && "text-[#a49e92] hover:text-[#f5bc8e]"
            )}
          >
            EN
          </button>
        </div>
        </div>
      </header>
      <div className="relative z-10 mx-auto flex min-h-[calc(100dvh-4rem)] w-full max-w-5xl items-center justify-center px-4 py-10">
        <div
          className={cn(
            "pointer-events-none absolute z-10",
            "left-1/2 top-[max(0.75rem,4vh)] -translate-x-1/2",
            "h-[20vh] w-[20vh] min-h-[96px] min-w-[96px] max-h-[220px] max-w-[220px]"
          )}
          aria-hidden
        >
          <img
            src={`${COMMON_IMAGES}/logo/logo.png`}
            alt="logo overlay"
            className="h-full w-full object-contain drop-shadow-[0_18px_30px_rgba(0,0,0,0.65)]"
            draggable={false}
          />
        </div>
        <div className="ui-glass-panel relative z-[1] w-full max-w-md rounded-2xl p-6 shadow-[0_30px_70px_rgba(0,0,0,0.45)]">
          <div className="relative z-[1] mb-6 text-center">
            <div className="text-2xl font-extrabold tracking-wide text-white">{t("auth.login")}</div>
          </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleLogin();
                }}
            className="relative z-[1] space-y-4"
              >
              <Input
                placeholder={t("auth.usernamePlaceholder")}
                value={formData.username}
                onChange={handleInputChange("username")}
                error={errors.username}
                inputClassName="text-sm"
                containerClassName="py-2"
              />
              <Input
                type="password"
                placeholder={t("auth.passwordPlaceholder")}
                value={formData.password}
                onChange={handleInputChange("password")}
                error={errors.password}
                inputClassName="text-sm"
                containerClassName="py-2"
              />
              <button
                type="submit"
                className={cn(
                "ui-glass-btn ui-glass-btn--accent mt-2 w-full rounded-xl px-4 py-3 text-sm",
                loading && "pointer-events-none opacity-70"
                )}
              >
              {loading ? t("common.loading") || "Loading..." : t("auth.login")}
              </button>
              </form>
              {apiError && (
            <p className="mt-3 text-center text-[#a86a6b] text-xs font-medium">
                  {apiError}
                </p>
              )}
          <button
            className="ui-glass-btn mt-4 w-full rounded-xl px-4 py-3 text-sm"
            onClick={() => router.push(seotdaPath("register"))}
            type="button"
          >
            {t("auth.signUp")}
          </button>
        </div>
      </div>
      {buildDateLabel && (
        <div className="absolute right-3 bottom-2 z-30 text-[10px] md:text-xs text-gray-400">
          build: {buildDateLabel}
        </div>
      )}
    </div>
  );
};

export default LoginPageClient;
